/*
 * Copyright © 2018-2024, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useState } from "react";
import { ClinicProfileSettingsResponse } from "../../../../models/clinic/profile/ClinicProfileSettingsResponse";
import { useFieldArray, useForm } from "react-hook-form";
import { strings } from "../../../../common/Strings/Strings";
import ClinicProfileApi from "../../../../api/ClinicProfileApi";
import { canManageClinic, getGeneralError } from "../../../../util/helperFunctions";
import AlertBox, { AlertType } from "../../../../components/AlertBox";
import Button from "../../../../components/Button";
import { defaultCalendarEndTime, defaultCalendarStartTime } from "../../../Calendar/calendarConfig";
import CheckBox from "../../../../components/ReactHookFormFields/General/CheckBox";
import Field from "../../../../components/ReactHookFormFields/General/Field";
import { SpecialOpeningHoursItemRequest } from "../../../../models/clinic/profile/SpecialOpeningHoursItemRequest";
import { Trash } from "../../../../common/Icons/Trash";
import { PlusIcon } from "../../../../common/Icons/PlusIcon";
import { ClinicResponse } from "../../../../models/clinic/ClinicResponse";

interface Props {
  clinic: ClinicResponse;
  settings: ClinicProfileSettingsResponse;
  setSettings: (settings: ClinicProfileSettingsResponse) => void;
}

interface OpeningHourItem {
  day: string;
  closed: boolean;
  startTime?: string;
  endTime?: string;
}

interface OpeningHoursForm {
  specialOpeningHours: OpeningHourItem[];
}

const defaultSpecialOpeningHours = (settings: ClinicProfileSettingsResponse) => {
  return settings.specialOpeningHours.map((item) => ({
    day: item.date.toString(),
    closed: !item.open,
    startTime: item.open?.startTime || settings?.calendarDayStartTime || defaultCalendarStartTime,
    endTime: item.open?.endTime || settings?.calendarDayEndTime || defaultCalendarEndTime,
  }));
};

const defaultFormValues = (settings: ClinicProfileSettingsResponse): OpeningHoursForm => ({
  specialOpeningHours: defaultSpecialOpeningHours(settings),
});

const SpecialOpeningHours: React.FC<Props> = (props: Props) => {
  const { clinic, settings, setSettings } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, errors },
  } = useForm<OpeningHoursForm>({
    mode: "onSubmit",
    defaultValues: defaultFormValues(settings),
  });

  const specialOpeningHoursArray = useFieldArray({ control, name: "specialOpeningHours" });
  const specialOpeningHours = watch("specialOpeningHours");

  useEffect(() => {
    reset(defaultFormValues(settings));
  }, [settings]);

  const saveSettings = async (data: OpeningHoursForm) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      const specialOpeningHours: SpecialOpeningHoursItemRequest[] = data.specialOpeningHours.map((item) => ({
        date: new Date(item.day),
        open:
          item.closed || !item.startTime || !item.endTime
            ? undefined
            : { startTime: item.startTime, endTime: item.endTime },
      }));

      const request = { specialOpeningHours };
      const response = await ClinicProfileApi.updateSpecialOpeningHours(clinic.id, request);
      setSettings(response.data);
      setSuccess(true);
    } catch (e) {
      setError(await getGeneralError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(saveSettings)}>
      <div className="tw-card divide-y divide-gray-200 dark:divide-gray-700">
        <div className="p-4 flex items-center">
          <p className="uppercase">{strings.clinicProfileSpecialOpeningHours}</p>
        </div>
        <div className="p-4 space-y-4">
          <Button
            variant="link"
            hidden={!canManageClinic(clinic)}
            onClick={() =>
              specialOpeningHoursArray.append({
                day: "",
                closed: true,
                startTime: settings?.calendarDayStartTime || defaultCalendarStartTime,
                endTime: settings?.calendarDayEndTime || defaultCalendarEndTime,
              })
            }
          >
            <div className="flex">
              <PlusIcon /> <span>{strings.clinicProfileSpecialOpeningHoursAddNew}</span>
            </div>
          </Button>
          {specialOpeningHoursArray.fields.map((item, index) => (
            <div className="tw-card p-4 space-y-4">
              <div className="flex space-x-4 items-center">
                <Field
                  register={register}
                  name={`specialOpeningHours.${index}.day`}
                  type="date"
                  error={errors.specialOpeningHours && errors.specialOpeningHours[index]?.day}
                  required
                />
                <div className="flex-1 text-right">
                  <Button
                    variant="icon"
                    onClick={() => specialOpeningHoursArray.remove(index)}
                    hidden={!canManageClinic(clinic)}
                  >
                    <Trash />
                  </Button>
                </div>
              </div>
              <div className="mb-4">
                <div className="flex align-middle space-x-2">
                  <div className="flex-1">
                    <label>{strings[item.day]}</label>
                    <CheckBox
                      name={`specialOpeningHours.${index}.closed`}
                      register={register}
                      label={strings.clinicClosed}
                      className="mt-8"
                      readOnly={!canManageClinic(clinic)}
                    />
                  </div>
                  <div className="flex-1">
                    <Field
                      type="time"
                      className="mb-0"
                      register={register}
                      name={`specialOpeningHours.${index}.startTime`}
                      label={strings.clinicOpensAt}
                      error={errors.specialOpeningHours && errors.specialOpeningHours[index]?.startTime}
                      readOnly={(specialOpeningHours && specialOpeningHours[index]?.closed) || !canManageClinic(clinic)}
                    />
                  </div>
                  <div className="flex-1">
                    <Field
                      type="time"
                      className="mb-0"
                      register={register}
                      name={`specialOpeningHours.${index}.endTime`}
                      label={strings.clinicClosesAt}
                      error={errors.specialOpeningHours && errors.specialOpeningHours[index]?.endTime}
                      readOnly={(specialOpeningHours && specialOpeningHours[index]?.closed) || !canManageClinic(clinic)}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
          <AlertBox message={error} />
          <AlertBox closeAble={false} hidden={!success} message={strings.savedSuccessfully} type={AlertType.SUCCESS} />
          <div className="text-right">
            <Button
              disabled={loading || !isDirty}
              loading={loading}
              type="submit"
              fullWidth={false}
              hidden={!canManageClinic(clinic)}
            >
              {strings.save}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default SpecialOpeningHours;
